import React, { useState, useEffect } from 'react';
import api from '../../api/axios';
import '../../styles/pages.css';

export default function Contacts() {
  const [activeTab, setActiveTab] = useState('contacts'); // 'contacts' or 'groups'
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [formType, setFormType] = useState(null); // 'contact', 'group', 'import'

  // Contacts state
  const [contacts, setContacts] = useState([]);
  const [groups, setGroups] = useState([]);
  const [newContact, setNewContact] = useState({ contact: '', group: '' });
  const [newGroup, setNewGroup] = useState({ groupName: '' });
  const [csvFile, setCsvFile] = useState(null);
  const [csvFileName, setCsvFileName] = useState('');
  const [loadingContacts, setLoadingContacts] = useState(false);
  const [loadingGroups, setLoadingGroups] = useState(false);
  const [importProgress, setImportProgress] = useState({ current: 0, total: 0, percentage: 0 });
  const [isImporting, setIsImporting] = useState(false);
  const [filterGroup, setFilterGroup] = useState('');

  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const entityId = user.entity;

  // Fetch contacts and groups on mount
  useEffect(() => {
    if (activeTab === 'contacts') {
      fetchContacts();
      fetchGroups();
    } else {
      fetchGroups();
    }
  }, [activeTab]);

  async function fetchContacts() {
    setLoadingContacts(true);
    try {
      const { data } = await api.get(`/api/contacts/${entityId}`);
      setContacts(data);
    } catch (error) {
      console.error('Error fetching contacts:', error);
      alert('Failed to fetch contacts');
    } finally {
      setLoadingContacts(false);
    }
  }

  async function fetchGroups() {
    setLoadingGroups(true);
    try {
      const { data } = await api.get(`/api/contact-groups/${entityId}`);
      setGroups(data);
    } catch (error) {
      console.error('Error fetching groups:', error);
      alert('Failed to fetch groups');
    } finally {
      setLoadingGroups(false);
    }
  }

  async function handleCreateContact(e) {
    e.preventDefault();
    if (!newContact.contact.trim()) {
      alert('Please enter a contact');
      return;
    }
    if (!newContact.group) {
      alert('Please select a group');
      return;
    }

    try {
      await api.post('/api/contacts', {
        contact: newContact.contact,
        contactGroup: newContact.group,
        entity: entityId,
      });
      setNewContact({ contact: '', group: '' });
      setShowCreateForm(false);
      fetchContacts();
    } catch (error) {
      alert(error.response?.data?.message || 'Error creating contact');
    }
  }

  async function handleCreateGroup(e) {
    e.preventDefault();
    if (!newGroup.groupName.trim()) {
      alert('Please enter a group name');
      return;
    }

    try {
      await api.post('/api/contact-groups', {
        groupName: newGroup.groupName,
        entity: entityId,
      });
      setNewGroup({ groupName: '' });
      setShowCreateForm(false);
      fetchGroups();
    } catch (error) {
      alert(error.response?.data?.message || 'Error creating group');
    }
  }

  async function handleDeleteContact(contactId) {
    if (window.confirm('Delete this contact?')) {
      try {
        await api.delete(`/api/contacts/${contactId}`);
        fetchContacts();
      } catch (error) {
        alert('Error deleting contact');
      }
    }
  }

  async function handleDeleteGroup(groupId) {
    if (window.confirm('Delete this group?')) {
      try {
        await api.delete(`/api/contact-groups/${groupId}`);
        fetchGroups();
      } catch (error) {
        alert('Error deleting group');
      }
    }
  }

  function handleCsvChange(e) {
    const file = e.target.files[0];
    if (file) {
      setCsvFileName(file.name);
      setCsvFile(file);
    }
  }

  async function handleCsvImport(e) {
    e.preventDefault();
    if (!csvFile) {
      alert('Please select a CSV file');
      return;
    }
    if (!newContact.group) {
      alert('Please select a target group');
      return;
    }

    try {
      const reader = new FileReader();
      reader.onload = async (event) => {
        const csv = event.target.result;
        const lines = csv.split('\n');

        const headerLine = lines[0].toLowerCase();
        const headers = headerLine.split(',').map(h => h.trim().toLowerCase());
        const msisdnIndex = headers.findIndex(h => h === 'msisdn' || h === 'phone' || h === 'phonenumber');

        if (msisdnIndex === -1) {
          alert('CSV must contain a "msisdn" column');
          return;
        }

        const phoneNumbers = [];
        for (let i = 1; i < lines.length; i++) {
          const line = lines[i].trim();
          if (line) {
            const fields = line.split(',');
            const phone = fields[msisdnIndex]?.trim();
            if (phone) {
              const normalized = normalizePhoneNumber(phone);
              if (normalized && normalized.startsWith('254') && normalized.length === 12) {
                phoneNumbers.push(normalized);
              }
            }
          }
        }

        if (phoneNumbers.length === 0) {
          alert('No valid phone numbers found in CSV');
          return;
        }

        setIsImporting(true);
        setImportProgress({ current: 0, total: phoneNumbers.length, percentage: 0 });

        // Simulate progress updates
        const progressInterval = setInterval(() => {
          setImportProgress(prev => {
            if (prev.current < prev.total) {
              const newCurrent = Math.min(prev.current + Math.floor(Math.random() * 10) + 5, prev.total);
              const percentage = Math.round((newCurrent / prev.total) * 100);
              return { ...prev, current: newCurrent, percentage };
            }
            return prev;
          });
        }, 300);

        try {
          const { data } = await api.post('/api/contacts/import', {
            contacts: phoneNumbers,
            contactGroup: newContact.group,
            entity: entityId,
          });

          clearInterval(progressInterval);
          setImportProgress({ current: phoneNumbers.length, total: phoneNumbers.length, percentage: 100 });

          setTimeout(() => {
            alert(`✓ Successfully imported ${data.imported} contacts\n✗ Failed: ${data.failed || 0}`);
            setCsvFile(null);
            setCsvFileName('');
            setNewContact({ contact: '', group: '' });
            setFormType(null);
            setShowCreateForm(false);
            setIsImporting(false);
            setImportProgress({ current: 0, total: 0, percentage: 0 });
            fetchContacts();
          }, 1000);
        } catch (error) {
          clearInterval(progressInterval);
          setIsImporting(false);
          setImportProgress({ current: 0, total: 0, percentage: 0 });
          alert(error.response?.data?.message || 'Error importing contacts');
        }
      };
      reader.readAsText(csvFile);
    } catch (error) {
      console.error('Error:', error);
      alert('Error processing CSV file');
    }
  }

  function normalizePhoneNumber(phone) {
    if (!phone) return null;
    let cleaned = phone.replace(/[^\d+]/g, '');
    if (cleaned.startsWith('+')) {
      cleaned = cleaned.substring(1);
    }
    if (cleaned.startsWith('0')) {
      cleaned = '254' + cleaned.substring(1);
    }
    if (cleaned.startsWith('254')) {
      return cleaned;
    }
    if (cleaned.length === 9) {
      return '254' + cleaned;
    }
    return cleaned;
  }

  function getFilteredContacts() {
    if (!filterGroup) {
      return contacts;
    }
    const groupId = parseInt(filterGroup);
    return contacts.filter(contact => {
      const contactGroupId = typeof contact.contactGroup === 'string'
        ? parseInt(contact.contactGroup)
        : contact.contactGroup;
      return contactGroupId === groupId;
    });
  }

  return (
    <div className="page-container">
      <div className="page-header">
        <h1>Contacts</h1>
        <p>Manage your contacts and groups</p>
      </div>

      <div className="tabs-container">
        <div className="tabs-header">
          <button
            className={`tab-button ${activeTab === 'contacts' ? 'active' : ''}`}
            onClick={() => {
              setActiveTab('contacts');
              setShowCreateForm(false);
            }}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
              <circle cx="9" cy="7" r="4" />
              <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
              <path d="M16 3.13a4 4 0 0 1 0 7.75" />
            </svg>
            All Contacts
            <span className="tab-badge">{contacts.length}</span>
          </button>
          <button
            className={`tab-button ${activeTab === 'groups' ? 'active' : ''}`}
            onClick={() => {
              setActiveTab('groups');
              setShowCreateForm(false);
            }}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
              <circle cx="9" cy="7" r="4" />
              <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
              <path d="M16 3.13a4 4 0 0 1 0 7.75" />
            </svg>
            Groups
            <span className="tab-badge">{groups.length}</span>
          </button>
        </div>

        {/* Contacts Tab */}
        {activeTab === 'contacts' && (
          <div className="tab-content">
            {!showCreateForm ? (
              <div className="tab-actions">
                <button className="btn-primary" onClick={() => {
                  setFormType('contact');
                  setShowCreateForm(true);
                }}>
                  + Add Contact
                </button>
                <button className="btn-secondary" onClick={() => {
                  setFormType('import');
                  setShowCreateForm(true);
                }}>
                  📥 Import CSV
                </button>
              </div>
            ) : (
              <div className="form-container">
                {formType === 'contact' && (
                  <div className="form-section">
                    <h3 className="form-section-title">Add New Contact</h3>
                    <form onSubmit={handleCreateContact}>
                      <div className="form-group">
                        <label>Phone Number</label>
                        <input
                          type="tel"
                          placeholder="0797083661 or 254797083661"
                          value={newContact.contact}
                          onChange={(e) => setNewContact({ ...newContact, contact: e.target.value })}
                          required
                        />
                      </div>
                      <div className="form-group">
                        <label>Select Group</label>
                        <select
                          value={newContact.group}
                          onChange={(e) => setNewContact({ ...newContact, group: e.target.value })}
                          required
                        >
                          <option value="">-- Choose a group --</option>
                          {groups.map((group) => (
                            <option key={group.id} value={group.id}>
                              {group.groupName}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="form-buttons">
                        <button type="submit" className="btn-primary">Add Contact</button>
                        <button
                          type="button"
                          className="btn-secondary"
                          onClick={() => {
                            setShowCreateForm(false);
                            setNewContact({ contact: '', group: '' });
                          }}
                        >
                          Cancel
                        </button>
                      </div>
                    </form>
                  </div>
                )}

                {formType === 'import' && (
                  <div className="form-section">
                    <h3 className="form-section-title">Import Contacts from CSV</h3>

                    {isImporting && (
                      <div className="import-progress">
                        <div className="progress-label">Importing contacts...</div>
                        <div className="progress-bar">
                          <div
                            className="progress-fill"
                            style={{ width: `${importProgress.percentage}%` }}
                          />
                        </div>
                        <div className="progress-text">
                          {importProgress.current} / {importProgress.total}
                        </div>
                        <div className="progress-percentage">
                          {importProgress.percentage}% Complete
                        </div>
                      </div>
                    )}

                    <form onSubmit={handleCsvImport}>
                      <div className="form-group">
                        <label>Select Group (target for import)</label>
                        <select
                          value={newContact.group}
                          onChange={(e) => setNewContact({ ...newContact, group: e.target.value })}
                          required
                          disabled={isImporting}
                        >
                          <option value="">-- Choose a group --</option>
                          {groups.map((group) => (
                            <option key={group.id} value={group.id}>
                              {group.groupName}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div className="form-group">
                        <label>Upload CSV File</label>
                        <div className="file-upload-wrapper">
                          <input
                            type="file"
                            accept=".csv"
                            onChange={handleCsvChange}
                            className="file-input"
                            required
                            disabled={isImporting}
                          />
                          <div className="file-upload-area">
                            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                              <polyline points="17 8 12 3 7 8" />
                              <line x1="12" y1="3" x2="12" y2="15" />
                            </svg>
                            {csvFileName ? (
                              <div>
                                <p className="file-name">✓ {csvFileName}</p>
                              </div>
                            ) : (
                              <div>
                                <p className="file-hint">Click to select CSV file</p>
                                <p className="file-hint-small">Must have msisdn column</p>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>

                      <div className="form-buttons">
                        <button type="submit" className="btn-primary" disabled={isImporting}>
                          {isImporting ? (
                            <>
                              <span className="spinner" style={{ marginRight: '0.5rem' }} />
                              Importing...
                            </>
                          ) : (
                            'Import Contacts'
                          )}
                        </button>
                        <button
                          type="button"
                          className="btn-secondary"
                          onClick={() => {
                            setShowCreateForm(false);
                            setCsvFile(null);
                            setCsvFileName('');
                            setNewContact({ contact: '', group: '' });
                            setImportProgress({ current: 0, total: 0, percentage: 0 });
                            setIsImporting(false);
                          }}
                          disabled={isImporting}
                        >
                          Cancel
                        </button>
                      </div>
                    </form>
                  </div>
                )}
              </div>
            )}

            {/* Filter Section */}
            {contacts.length > 0 && (
              <div className="filter-section">
                <label htmlFor="groupFilter">Filter by Group:</label>
                <select
                  id="groupFilter"
                  value={filterGroup}
                  onChange={(e) => {
                    console.log('Filter changed to:', e.target.value);
                    setFilterGroup(e.target.value);
                  }}
                  className="filter-select"
                >
                  <option value="">All Contacts ({contacts.length})</option>
                  {groups.map((group) => {
                    const countInGroup = contacts.filter(c => {
                      const contactGroupId = typeof c.contactGroup === 'string'
                        ? parseInt(c.contactGroup)
                        : c.contactGroup;
                      return contactGroupId === group.id;
                    }).length;
                    return (
                      <option key={group.id} value={group.id}>
                        {group.groupName} ({countInGroup})
                      </option>
                    );
                  })}
                </select>
              </div>
            )}

            {/* Contacts List */}
            <div className="contacts-list">
              {filterGroup && (
                <div className="filter-info">
                  Showing {getFilteredContacts().length} of {contacts.length} contacts
                </div>
              )}
              {loadingContacts ? (
                <p className="text-muted">Loading contacts...</p>
              ) : contacts.length === 0 ? (
                <p className="text-muted">No contacts yet. Add one to get started!</p>
              ) : getFilteredContacts().length === 0 ? (
                <p className="text-muted">No contacts in this group.</p>
              ) : (
                <div className="list-grid">
                  {getFilteredContacts().map((contact) => (
                    <div key={contact.id} className="list-item">
                      <div className="item-content">
                        <div className="item-phone">{contact.contact}</div>
                        <div className="item-group">{contact.groupName}</div>
                      </div>
                      <button
                        className="item-delete"
                        onClick={() => handleDeleteContact(contact.id)}
                        title="Delete"
                      >
                        ✕
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Groups Tab */}
        {activeTab === 'groups' && (
          <div className="tab-content">
            {!showCreateForm ? (
              <div className="tab-actions">
                <button className="btn-primary" onClick={() => {
                  setFormType('group');
                  setShowCreateForm(true);
                }}>
                  + Create Group
                </button>
              </div>
            ) : (
              <div className="form-container">
                {formType === 'group' && (
                  <div className="form-section">
                    <h3 className="form-section-title">Create New Group</h3>
                    <form onSubmit={handleCreateGroup}>
                      <div className="form-group">
                        <label>Group Name</label>
                        <input
                          type="text"
                          placeholder="e.g., VIP Customers"
                          value={newGroup.groupName}
                          onChange={(e) => setNewGroup({ groupName: e.target.value })}
                          required
                        />
                      </div>
                      <div className="form-buttons">
                        <button type="submit" className="btn-primary">Create Group</button>
                        <button
                          type="button"
                          className="btn-secondary"
                          onClick={() => {
                            setShowCreateForm(false);
                            setNewGroup({ groupName: '' });
                          }}
                        >
                          Cancel
                        </button>
                      </div>
                    </form>
                  </div>
                )}
              </div>
            )}

            {/* Groups List */}
            <div className="groups-list">
              {loadingGroups ? (
                <p className="text-muted">Loading groups...</p>
              ) : groups.length === 0 ? (
                <p className="text-muted">No groups yet. Create one to get started!</p>
              ) : (
                <div className="list-grid">
                  {groups.map((group) => (
                    <div key={group.id} className="list-item">
                      <div className="item-content">
                        <div className="item-group-name">{group.groupName}</div>
                        <div className="item-count">{group.contactCount || 0} contacts</div>
                      </div>
                      <button
                        className="item-delete"
                        onClick={() => handleDeleteGroup(group.id)}
                        title="Delete"
                      >
                        ✕
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
